import './BenefitsWidget.scss';

import React, { useCallback, useEffect, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button';
import Link from 'fe-design-base/molecules/Link';
import { fromJS } from 'immutable';
import PropTypes from 'prop-types';

import { ACCRUE_URL } from 'features/payroll/constants';

import AddButton from 'components/clickables/AddButton';

import { cxHelpers } from 'util/className';

import BenefitForm from './BenefitForm';
import BenefitsList from './BenefitsList';

const { cx } = cxHelpers('BenefitsWidget');

const BenefitsWidget = ({ user, updateUserInfo, showFormWithSteps }) => {
  const [userObj, setUserObj] = useState(user);
  const isGuideline401kConfigured =
    user?.get('payroll_guideline_component_status') === 'done';
  const isSimplyInsuredConfigured =
    user?.get('payroll_simply_insured_component_status') === 'done';

  useEffect(() => {
    setUserObj(fromJS(user));
  }, [user]);

  const [isFormVisible, setIsFormVisible] = useState(false);
  const handleAddBenefit = useCallback(() => {
    setIsFormVisible(true);
  }, []);

  const handleOnCancel = useCallback(() => {
    setIsFormVisible(false);
  }, []);

  const handleOnSaveCompleted = useCallback(response => {
    setIsFormVisible(false);
    setUserObj(fromJS(response));
  }, []);

  if (!userObj.get('show_benefits_widget')) return null;

  return (
    <Box className={cx()} mb={32}>
      <Box mb={12}>
        <Text
          variant={showFormWithSteps ? 'heading4' : 'heading3'}
          i18n="team.employee_profile.tax_deductions.title"
          color={showFormWithSteps && 'mono900'}
        />
      </Box>
      {isGuideline401kConfigured && (
        <Box p={32} bradiusm bw={1} bcolor="mono100" mb={12} row gap={4}>
          <Text
            variant="body"
            i18n="features.benefits.retirement.team_profile.alert_401_configured.title"
          />
          <Link
            href={ACCRUE_URL}
            target="_blank"
            uxElement="benefits_widget_guideline_link"
            endIcon="ExternalLink"
          >
            <Text
              variant="bodyBold"
              i18n="features.benefits.retirement.team_profile.alert_401_configured.link"
            />
          </Link>
        </Box>
      )}
      <Box bw={!showFormWithSteps ? 1 : 0} pb={8} bcolor="mono100" row bradiusm>
        <Box grow={2}>
          <BenefitsList
            user={userObj}
            onSaveCompleted={handleOnSaveCompleted}
            updateUserInfo={updateUserInfo}
            showFormWithSteps={showFormWithSteps}
            isGuideline401kConfigured={isGuideline401kConfigured}
            isSimplyInsuredConfigured={isSimplyInsuredConfigured}
          />
          {isFormVisible ? (
            <BenefitForm
              onCancel={handleOnCancel}
              onSaveCompleted={handleOnSaveCompleted}
              user={userObj}
              updateUserInfo={updateUserInfo}
              showFormWithSteps={showFormWithSteps}
              isGuideline401kConfigured={isGuideline401kConfigured}
              isSimplyInsuredConfigured={isSimplyInsuredConfigured}
            />
          ) : (
            <Box mt={8}>
              {showFormWithSteps ? (
                <Button
                  startIcon="Add"
                  variant="tertiary"
                  onClick={handleAddBenefit}
                >
                  <Text
                    variant="bodyBold"
                    i18n="team.employee_profile.tax_deductions.add_benefit"
                    color="purple500"
                  />
                </Button>
              ) : (
                <AddButton
                  onClick={handleAddBenefit}
                  i18n="team.employee_profile.tax_deductions.add_benefit"
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

BenefitsWidget.propTypes = {
  user: ImmutablePropTypes.map.isRequired,
  updateUserInfo: PropTypes.func.isRequired,
};

export default BenefitsWidget;
